import { CONTROL_PANEL_PATHS } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE CONSOLE'S OWN CLIENT. NOT A SHARED ONE, AND NOT A SHARED SESSION.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` S-6, and the outgoing project's own words for the same decision: *"a superadmin is
 * a separate principal set with its own token, and sharing a client between the two applications is
 * the first step toward sharing a session."* Its route table said the same thing about route tables.
 * Both were right, and both were written after having tried it the other way.
 *
 * ⚠️ **What is deliberately ABSENT: refresh, single-flight, retry-on-401.** `apps/web`'s client has
 * all three and needs them — a GoTrue access token expires in an hour and a screen mid-edit must not
 * become a login form. An opaque 8-hour operator token has **nothing to refresh**, and a 401 means
 * the session is over: revoked, expired, or the operator disabled. The client's job is to notice and
 * hand the caller a signal, not to try again.
 *
 * ⚠️ **NO `credentials: 'include'`, and that is not an oversight either.** The product sends it
 * because its refresh token is an httpOnly cookie. There is no operator cookie: the token is in
 * `sessionStorage` and travels as a bearer, so asking for credentials would only widen what the
 * browser attaches for no benefit.
 */

/**
 * ⚠️ **Read at CALL time and refused when empty**, for the reason `apps/web/src/api.ts` records: a
 * `const BASE = import.meta.env…` at module scope captures the value at import, which makes it
 * untestable — and a missing variable becomes the string `undefined`, so every request goes to
 * `undefined/control-panel/tenants` and the screen reports the API as down. A configuration mistake
 * that presents as a server fault is the most expensive kind.
 */
export function apiBase(): string {
  const base: unknown = import.meta.env.VITE_API_BASE_URL;
  if (typeof base !== 'string' || base.trim() === '') {
    throw new Error(
      'VITE_API_BASE_URL is not set, so the console does not know where the API is. ' +
        'It is documented in .env.example; Vite only exposes variables prefixed VITE_.',
    );
  }
  return base.replace(/\/$/, '');
}

export function apiUrl(path: string): string {
  /**
   * ⚠️⚠️ **A PATH THAT IS NOT A PATH IS REFUSED HERE, BECAUSE THE ALTERNATIVE BLAMES THE SERVER.**
   * `apiBase()` above already refuses an empty base, and its reason applies to the other half of the
   * string verbatim: *"a configuration mistake that presents as a server fault is the most expensive
   * kind."*
   *
   * ⚠️ **Measured, from the running app.** A stale Vite pre-bundle (hard-won C5-8, S2-5) served the
   * browser a copy of `@forecastify/shared` from before `CONTROL_PANEL_PATHS.plans` existed, so the
   * screen called this with `undefined`, the template produced `http://localhost:3000undefined`,
   * `fetch` rejected, and the client answered `unreachable` — *"the server is not responding"*. The
   * server was fine, and nothing in the message pointed at the dependency cache.
   */
  /* ⚠️ Widened to `unknown` first, and `apiBase()` above uses the same idiom for the same reason:
   * the signature says this cannot happen, the runtime says otherwise, and a check the types call
   * impossible is exactly the check worth having. Narrowing here also keeps lint honest — with
   * `path` still typed `string`, `String(path)` reads as a pointless conversion. */
  const given: unknown = path;
  if (typeof given !== 'string' || !given.startsWith('/')) {
    throw new Error(
      `apiUrl was given ${String(given)}, which is not a path. A missing constant reaching here ` +
        'usually means a stale dependency pre-bundle — run `pnpm frontend:fresh` (see ' +
        'docs/environment/hard-won.md C5-8) rather than looking for a server fault.',
    );
  }
  return `${apiBase()}${given}`;
}

/**
 * What every call answers.
 *
 * ⚠️⚠️ **A RESULT, NOT A THROW, AND `code` IS WHAT A SCREEN BRANCHES ON.** CLAUDE.md rule 5:
 * *"clients branch on `code`, never on `message`"*. A rejected promise would put the envelope's code
 * inside an `Error`'s message, where the only way to read it is a substring match — which is the
 * exact thing the rule forbids, arrived at by accident.
 *
 * ⚠️ `code: 'unreachable'` is this layer's own, for the case where there is no response at all:
 * `fetch` REJECTS when the API is unreachable (wrong port, not started, offline), and a screen that
 * does not distinguish that from a refusal tells somebody their password is wrong when the server is
 * simply down.
 */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly code: string; readonly status: number };

const UNREACHABLE = { ok: false as const, code: 'unreachable', status: 0 };

/**
 * What a call takes.
 *
 * ⚠️ **`headers` is narrowed to a plain record, not `RequestInit['headers']`.** That type is a union
 * including `string[][]`, and spreading an array into an object gives a list of indices — lint says
 * so, and the result would be a header called `0`. Named as a type rather than inlined because
 * `session.request` passes it through, and two spellings of the same shape is one edit from
 * disagreeing.
 */
export type ApiInit = Omit<RequestInit, 'headers'> & {
  readonly token?: string | null;
  readonly headers?: Readonly<Record<string, string>>;
};

export async function apiCall<T>(
  path: string,
  /**
   * ⚠️ **`headers` is narrowed to a plain record, not `RequestInit['headers']`.** That type is a
   * union including `string[][]`, and spreading an array into an object gives you a list of indices
   * — which lint catches and which would otherwise send a header called `0`. Nothing here needs the
   * other two forms.
   */
  init: ApiInit = {},
): Promise<ApiResult<T>> {
  const { token, headers, ...rest } = init;

  const response = await fetch(apiUrl(path), {
    ...rest,
    headers: {
      'content-type': 'application/json',
      ...(token === undefined || token === null ? {} : { authorization: `Bearer ${token}` }),
      ...headers,
    },
  }).catch(() => null);

  if (response === null) return UNREACHABLE;

  /* 204, which sign-out answers. `.json()` on an empty body rejects. */
  if (response.status === 204) return { ok: true, value: undefined as T };

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    /**
     * ⚠️ **A response that is not the envelope is `unknown`, not a crash.** Anything answering on
     * the API's port that is not the API — a proxy error page, a captive portal, another project's
     * dev server — returns a body that is HTML. `apps/web` lost an entry suite to exactly this.
     */
    const code = (body as { error?: { code?: unknown } } | null)?.error?.code;
    return {
      ok: false,
      code: typeof code === 'string' ? code : 'unknown',
      status: response.status,
    };
  }

  return { ok: true, value: body as T };
}

export { CONTROL_PANEL_PATHS };
